// Global Types
import type {
  $Model,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  docRoute,
  docType,
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  getFileUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  getDateTimeFormats,
  getDocumentUrl,
} from 'common.helpers/dist/browser';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import LabelQuantity from '../../../common/LabelQuantity';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaDocument'>>;
  loading: boolean;
};

class DocumentsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaDocumentsSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaDocument') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  renderTable() {
    const {
      main,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const formats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;

    return (
      <table className={'table table_hoverable w-full'}>
        <thead>
          <tr>
            <th className={'text-left uppercase'}>
              {t('No.')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Title')}
            </th>
            <th className={'text-center uppercase'}>
              {t('Date published')}
            </th>
            <th className={'text-right uppercase'}>
              <LabelQuantity value={data.length} />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaDocument'>, index: number) => (
            <tr key={dataItem.id}>
              <td>
                {`#${index + 1}`}
              </td>
              <td>
                <span className={'text-base'}>
                  {dataItem.Name}
                </span>
              </td>
              <td className={'text-center'}>
                <div>
                  {moment(dataItem.PublishDate).fromNow()}
                </div>
                <small>
                  {moment(dataItem.PublishDate).format(dateTimeFormat)}
                </small>
              </td>
              <td className={'text-right'}>
                <a
                  className={'btn btn_primary btn_outlined uppercase whitespace-nowrap py-1'}
                  href={dataItem.File ? getFileUrl({
                    cdnUrl: config.CDN_URL,
                    file: dataItem.File,
                  }) : getDocumentUrl({
                    cdnUrl: config.CDN_URL,
                    docRoute: docRoute.regattaDocument,
                    docType: docType.pdf,
                    id: dataItem.id,
                    language: i18next.language as $Language,
                  })}
                  rel={'noreferrer'}
                  target={'_blank'}
                  title={t('View document')}
                >
                  <em className={'la la-file text-2xl'} />
                  <span className={'ml-2 hidden sm:block'}>
                    {t('View document')}
                  </span>
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <Section
        id={webSections[webPages.regattaPage].documents}
        title={t('Documents')}
      >
        <Loader
          fade
          loading={loading === true}
        >
          {data.length > 0 ? (
            <div className={'overflow-x-auto'}>
              {this.renderTable()}
            </div>
          ) : (
            <NoData label={t('No published documents yet')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default DocumentsSectionView;
